import { useCallback, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Share } from "lucide-react";
import { parseAsBoolean, useQueryState } from "nuqs";
import { useForm } from "react-hook-form";
import { z } from "zod";

import type { RouterOutputs } from "@acme/api";
import { Button } from "@acme/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@acme/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@acme/ui/form";
import { InputWithCopy } from "@acme/ui/input-with-copy";
import { Label } from "@acme/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@acme/ui/select";

import { useSession } from "~/utils/auth";
import { useSharedSessions } from "../../_hooks/use-shared-sessions";

// Define form schema
const ShareFormSchema = z.object({
  shareType: z.enum(["public", "private", "organization"]),
  organizationId: z.string().optional(),
});

type ShareFormValues = z.infer<typeof ShareFormSchema>;

export function ShareModal() {
  const [isOpen, setIsOpen] = useQueryState("share", parseAsBoolean);
  const [shareInfo, setShareInfo] = useState<
    RouterOutputs["sharedAstGrepStudioSession"]["share"] | null
  >(null);
  const [shareLink, setShareLink] = useState<string | null>(null);

  const session = useSession();
  const isAuthenticated = session.status === "authenticated";

  const {
    shareSession,
    isSharing,
    updateShareType,
    organizations,
    isLoadingOrganizations,
  } = useSharedSessions();

  // Initialize the form
  const form = useForm<ShareFormValues>({
    resolver: zodResolver(ShareFormSchema),
    defaultValues: {
      shareType: "public",
      organizationId: undefined,
    },
  });

  // Generate the share link when modal opens
  useEffect(() => {
    if (isOpen === true && !shareLink) {
      void shareSession()
        .then(({ shareInfo, shareUrl }) => {
          setShareInfo(shareInfo);
          setShareLink(shareUrl);
        })
        .catch((error) => {
          console.error("Failed to generate share link:", error);
        });
    }
  }, [isOpen, shareLink, shareSession]);

  // Handle modal open/close
  const handleOnOpenChange = useCallback(
    (open: boolean) => {
      void setIsOpen(open ? true : null);
    },
    [setIsOpen],
  );

  // Apply sharing preferences
  const onSubmit = useCallback(
    async (values: ShareFormValues) => {
      if (!isAuthenticated || !shareInfo) return;

      await updateShareType({
        id: shareInfo.id,
        shareType: values.shareType,
        organizationId:
          values.shareType === "organization"
            ? values.organizationId
            : undefined,
      });

      // remove dirty state
      form.reset(values);
    },
    [form, isAuthenticated, shareInfo, updateShareType],
  );

  return (
    <Dialog
      open={isOpen === true}
      onOpenChange={handleOnOpenChange}
      modal={true}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5">
            {shareInfo?.shareId ? (
              <Share className="text-muted-foreground size-5" />
            ) : (
              <Loader2 className="text-muted-foreground size-5 animate-spin" />
            )}
            {shareInfo?.shareId ? "Share" : "Sharing..."}
          </DialogTitle>
          <DialogDescription>
            {isAuthenticated
              ? "Choose how you want to share your session"
              : "Anyone with the link can access this session"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Share Link Section */}
          <div className="space-y-2">
            <Label htmlFor="share-link">Share Link</Label>
            <InputWithCopy
              id="share-link"
              className="text-sm"
              value={shareLink ?? ""}
              placeholder="Generating link..."
              copySuccessMessage="Link copied to clipboard"
              copyErrorMessage="Failed to copy link to clipboard"
            />
          </div>

          {/* Access Control Section */}
          {isAuthenticated && (
            <div className="space-y-3 border-t pt-4">
              <Label className="text-muted-foreground text-sm font-medium">
                Access Control
              </Label>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="shareType"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <FormLabel className="text-sm">
                          Who can access
                        </FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger id="share-type" className="h-auto">
                              <SelectValue placeholder="Select who can access this session" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="public">
                              <div className="flex flex-col items-start">
                                <span>Anyone with the link</span>
                                <span className="text-muted-foreground text-sm">
                                  Public access
                                </span>
                              </div>
                            </SelectItem>
                            <SelectItem value="private">
                              <div className="flex flex-col items-start">
                                <span>Only me</span>
                                <span className="text-muted-foreground text-sm">
                                  Private access
                                </span>
                              </div>
                            </SelectItem>
                            {organizations.length > 0 && (
                              <SelectItem value="organization">
                                <div className="flex flex-col items-start">
                                  <span>My organization</span>
                                  <span className="text-muted-foreground text-sm">
                                    Organization members only
                                  </span>
                                </div>
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Nested Organization Selection */}
                  {form.watch("shareType") === "organization" && (
                    <div className="border-muted ml-4 space-y-2 border-l-2 pl-4">
                      <FormField
                        control={form.control}
                        name="organizationId"
                        render={({ field }) => (
                          <FormItem className="space-y-2">
                            <FormLabel className="text-muted-foreground text-sm">
                              Select organization
                            </FormLabel>

                            <Select
                              value={field.value}
                              onValueChange={field.onChange}
                              disabled={
                                organizations.length === 0 ||
                                isLoadingOrganizations
                              }
                            >
                              <FormControl>
                                <SelectTrigger
                                  id="organization"
                                  className="h-auto"
                                >
                                  <SelectValue
                                    placeholder={
                                      isLoadingOrganizations
                                        ? "Loading organizations..."
                                        : organizations.length === 0
                                          ? "No organizations available"
                                          : "Select an organization"
                                    }
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {organizations.map((org) => (
                                  <SelectItem key={org.id} value={org.id}>
                                    {org.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex-1"></div>

                    {form.formState.isDirty && (
                      <Button
                        type="submit"
                        size="sm"
                        loading={isSharing}
                        disabled={
                          !shareLink ||
                          (form.watch("shareType") === "organization" &&
                            !form.watch("organizationId"))
                        }
                      >
                        {isSharing ? "Saving..." : "Apply"}
                      </Button>
                    )}
                  </div>
                </form>
              </Form>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Hook for share modal state
export function useShareModal() {
  return useQueryState("share", parseAsBoolean);
}
